import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiRequest } from '../services/queryClient';
import { api } from '../services/api';
import { showCartSuccess, showApiError } from '@/lib/toast-utils';
import { useUser } from '@/contexts/UserContext';
import type { Cart, CartItem, Photo, Event } from '@/types/schema';

export interface CartItemWithDetails extends CartItem {
  photo: Photo & {
    event: Event;
  };
}

export interface CartWithDetails extends Omit<Cart, 'items'> {
  items: CartItemWithDetails[];
}

export interface UseCartReturn {
  cart: CartWithDetails | null;
  isLoading: boolean;
  error: string | null;
  totalAmount: number;
  totalItems: number;
  addToCart: (photoId: string, quantity?: number) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  removeFromCart: (itemId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
  closeCart: () => Promise<void>;
}

export function useCart(): UseCartReturn {
  const [cart, setCart] = useState<CartWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isLoggedIn } = useUser();
  // Removido useToast - agora usando funções utilitárias

  // Calcular totais com verificação mais robusta
  const totalAmount = useMemo(() => {
    if (!cart?.items || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce((sum, item) => {
      // O backend já retorna unitPrice e quantity como números
      const unitPrice = Number(item.unitPrice) || 0;
      const quantity = Number(item.quantity) || 0;
      const itemTotal = unitPrice * quantity;



      return sum + itemTotal;
    }, 0);
  }, [cart?.items]);

  const totalItems = useMemo(() => {
    if (!cart?.items || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce((sum, item) => sum + item.quantity, 0);
  }, [cart?.items]);

  // Buscar carrinho do usuário
  const refreshCart = useCallback(async () => {

    // Não fazer requisição se o usuário não estiver logado
    if (!isLoggedIn) {

      setCart(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get('/cart');
      setCart(response?.data.data || response);
    } catch (err: any) {
      if (err.response?.status === 404) {
        // Carrinho não existe ainda, isso é normal
        setCart(null);
      } else {
        setError(err.response?.data?.message || 'Erro ao carregar carrinho');
        console.error('Erro ao buscar carrinho:', err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isLoggedIn]);

  // Adicionar item ao carrinho
  const addToCart = useCallback(async (photoId: string, quantity: number = 1) => {
    // Não fazer requisição se o usuário não estiver logado
    if (!isLoggedIn) {

      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      // Buscar informações da foto para obter o preço
      const photoResponse = await api.get(`/photos/${photoId}`);
      const photo = photoResponse.data.data; // A resposta já vem estruturada corretamente

      // Validar se a foto tem evento associado
      if (!photo.eventId) {
        throw new Error('Foto não está associada a nenhum evento');
      }

      // Validar tipo de monetização
      if (photo.monetizationType === 'free') {
        throw new Error('Este evento não permite venda de fotos');
      }

      // O preço pode ser calculado dinamicamente pelo backend
      // Se não tem preço definido, o backend usará um preço padrão
      if (photo.pricePerPhoto === undefined || photo.pricePerPhoto === null) {
        console.log('🔍 [useCart] addToCart - preço não definido, será calculado pelo backend');
      }


      // Preparar dados JSON com informações do evento e fotógrafo
      const eventData = {
        id: photo.eventId,
        title: photo.event?.title || 'Evento não especificado',
        author: {
          name: photo.photographer?.name || 'Não especificado',
          email: photo.photographer?.email || 'Não especificado'
        }
      };

      const photoData = {
        id: photo.id,
        filename: photo.filename,
        original_filename: photo.original_filename,
        storageurl: photo.storageurl,
        photographer: {
          name: photo.photographer?.name || 'Não especificado',
          email: photo.photographer?.email || 'Não especificado'
        }
      };

      const cartItemData = {
        photoId,
        quantity,
        unitPrice: photo.pricePerPhoto || 0, // O backend calculará o preço correto
        data: {
          event: eventData,
          photo: photoData
        }
      };

      const response = await api.post('/cart/items', cartItemData);

      // Atualizar carrinho após adicionar
      await refreshCart();

      // Verificar se foi uma atualização de quantidade ou novo item
      const isUpdate = response.data?.message?.includes('atualizada');

      showCartSuccess(isUpdate ? 'update' : 'add');

    } catch (err: any) {
      console.error('🔍 [useCart] addToCart - erro completo:', err);
      console.error('🔍 [useCart] addToCart - err.response:', err.response);
      console.error('🔍 [useCart] addToCart - err.message:', err.message);

      let errorMessage = 'Erro ao adicionar item ao carrinho';

      if (err.response?.status === 404) {
        errorMessage = 'Foto não encontrada';
      } else if (err.response?.status === 400) {
        errorMessage = err.response?.data?.message || 'Dados inválidos';
      } else if (err.response?.status === 401) {
        errorMessage = 'Você precisa estar logado para adicionar itens ao carrinho';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);

      showApiError(err);

      console.error('Erro ao adicionar ao carrinho:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [refreshCart, isLoggedIn]);

  // Remover item do carrinho
  const removeFromCart = useCallback(async (itemId: string) => {
    // Não fazer requisição se o usuário não estiver logado
    if (!isLoggedIn) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await api.delete(`/cart/items/${itemId}`);

      // Atualizar carrinho após remover
      await refreshCart();

      showCartSuccess('remove');
    } catch (err: any) {
      let errorMessage = 'Erro ao remover item do carrinho';

      if (err.response?.status === 404) {
        errorMessage = 'Item não encontrado no carrinho';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      setError(errorMessage);

      showApiError(err);

      console.error('Erro ao remover do carrinho:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [refreshCart, isLoggedIn]);

  // Atualizar quantidade de um item
  const updateQuantity = useCallback(async (itemId: string, quantity: number) => {
    // Não fazer requisição se o usuário não estiver logado
    if (!isLoggedIn || !cart) return;

    // Salvar estado anterior para possível rollback
    const previousCart = { ...cart };

    try {
      setError(null);

      if (quantity <= 0) {
        await removeFromCart(itemId);
        return;
      }

      // Atualização otimista: atualizar estado local imediatamente
      const updatedItems = cart.items.map(item =>
        item.id === itemId ? { ...item, quantity } : item
      );

      setCart({
        ...cart,
        items: updatedItems
      });

      // Fazer chamada da API em background
      await api.patch(`/cart/items/${itemId}`, {
        quantity,
      });

      showCartSuccess('update');
    } catch (err: any) {
      // Rollback: reverter para o estado anterior em caso de erro
      setCart(previousCart);

      let errorMessage = 'Erro ao atualizar quantidade';

      if (err.response?.status === 404) {
        errorMessage = 'Item não encontrado no carrinho';
      } else if (err.response?.status === 400) {
        errorMessage = err.response?.data?.message || 'Quantidade inválida';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      setError(errorMessage);

      showApiError(err);

      console.error('Erro ao atualizar quantidade:', err);
      throw err;
    }
  }, [cart, isLoggedIn, removeFromCart]);

  // Limpar carrinho
  const clearCart = useCallback(async () => {
    // Não fazer requisição se o usuário não estiver logado
    if (!isLoggedIn) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await api.delete('/cart');

      // Limpar estado local
      setCart(null);

      showCartSuccess('clear');
    } catch (err: any) {
      let errorMessage = 'Erro ao limpar carrinho';

      if (err.response?.status === 404) {
        errorMessage = 'Carrinho não encontrado';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      setError(errorMessage);

      showApiError(err);

      console.error('Erro ao limpar carrinho:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [isLoggedIn]);

  // Fechar carrinho (mudar status para Closed)
  const closeCart = useCallback(async () => {
    // Não fazer requisição se o usuário não estiver logado
    if (!isLoggedIn) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);


      await api.post('/cart/close');

      // Atualizar carrinho após fechar
      await refreshCart();


    } catch (err: any) {
      let errorMessage = 'Erro ao fechar carrinho';

      if (err.response?.status === 404) {
        errorMessage = 'Carrinho não encontrado';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      setError(errorMessage);

      showApiError(err);

      console.error('Erro ao fechar carrinho:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [isLoggedIn, refreshCart]);

  // Carregar carrinho na inicialização apenas se logado
  useEffect(() => {
    if (isLoggedIn) {
      refreshCart();
    }
  }, [refreshCart, isLoggedIn]);

  return {
    cart,
    isLoading,
    error,
    totalAmount,
    totalItems,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    refreshCart,
    closeCart,
  };
}

// Hook para verificar se uma foto está no carrinho
export function useIsInCart(photoId: string) {
  const { cart } = useCart();

  const cartItem = cart?.items.find(item => item.photoId === photoId);

  return {
    isInCart: !!cartItem,
    quantity: cartItem?.quantity || 0,
    cartItem,
  };
}